import React, { useState, useEffect, useMemo } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { 
  collection, 
  onSnapshot, 
  doc, 
  deleteDoc,
  addDoc,
  updateDoc,
  query,
  where
} from "firebase/firestore";
import {
  Search, Trash2, Printer, Filter, 
  Eye, Users, Activity, Edit3, Plus, X, Stethoscope, User as UserIcon, Calendar, PieChart as PieChartIcon, Download
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const INITIAL_FORM_STATE = {
  patientName: "", patientMobile: "", patientAge: "", patientGender: "Male",
  doctorName: "", prescriptionNotes: "",
  sphRight: "", cylRight: "", axisRight: "", pdRight: "", vaRight: "", addRight: "", nvSphRight: "", nvCylRight: "", nvAxisRight: "",
  sphLeft: "", cylLeft: "", axisLeft: "", pdLeft: "", vaLeft: "", addLeft: "", nvSphLeft: "", nvCylLeft: "", nvAxisLeft: "",
  clSphRight: "", clCylRight: "", clAxisRight: "", clSphLeft: "", clCylLeft: "", clAxisLeft: "",
  lensTypes: []
};

const ALL_LENS_TYPES = ['Single Vision', 'Bifocal', 'Progressive', 'Contact Lens', 'Constant Use', 'Reading Wear', 'Distance Wear', 'Trifocal'];
const COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2'];

export default function Prescription() {
  const [activeBranchId, setActiveBranchId] = useState(""); 
  const [activeStoreName, setActiveStoreName] = useState("Loading Store...");
  const [activeStoreAdress, setActiveStoreAdress] = useState("Loading Address...");
  const [activeStoreNumber, setActiveStoreNumber] = useState("Loading Landline...");
  const [prescriptions, setPrescriptions] = useState([]);
  
  // Filter States with new additions
  const [filters, setFilters] = useState({ 
    query: "", 
    startDate: "", 
    endDate: "", 
    doctorName: "", 
    lensType: "",
    recordsPerPage: 10 
  });

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [activeTab, setActiveTab] = useState("eyewear");

  // --- 1. DETECT USER UID & FETCH MATCHING STORE ---
  useEffect(() => {
    const auth = getAuth();
    let unsubStores = () => {};

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Query stores collection where linkedAuthUid matches the user's uid
        const storeQuery = query(
          collection(db, "stores"), 
          where("linkedAuthUid", "==", user.uid)
        );
        
        unsubStores = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0]; 
            const data = storeDoc.data();
            
            setActiveBranchId(storeDoc.id);
            setActiveStoreName(data.storeId || "Unnamed Store");
            setActiveStoreAdress(data.address || "CHANDRAKANTI COMPLEX OPP KHAJPURA, PATNA - 800014, Bihar, India" );
            setActiveStoreNumber(data.phone || "06123590450");
          } else {
            console.warn("No matching store found for this user UID.");
            setActiveBranchId("");
            setActiveStoreName("No Assigned Store");
          }
        });
      } else {
        setActiveBranchId("");
        setActiveStoreName("Please Log In");
      }
    });

    return () => {
      unsubAuth();
      unsubStores();
    };
  }, []);

  // --- 2. FETCH STORE SPECIFIC PRESCRIPTIONS ---
  useEffect(() => {
    if (!activeBranchId) return;
    const unsub = onSnapshot(collection(db, "stores", activeBranchId, "prescriptions"), (snap) => {
      setPrescriptions(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)));
    });
    return () => unsub();
  }, [activeBranchId]);

  // Extract unique doctors for the dropdown
  const uniqueDoctors = useMemo(() => {
    const docs = new Set(prescriptions.map(p => p.doctor?.name || p.doctorName).filter(Boolean));
    return Array.from(docs);
  }, [prescriptions]);

  // --- FILTERING ---
  const filteredData = useMemo(() => {
    let result = prescriptions;

    if (filters.query) {
      const q = filters.query.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(q) || 
        p.phone?.includes(q) || 
        p.doctorName?.toLowerCase().includes(q) ||
        p.patient?.name?.toLowerCase().includes(q) ||
        p.patient?.mobile?.includes(q)
      );
    }

    if (filters.startDate) {
      result = result.filter(p => new Date(p.timestamp) >= new Date(filters.startDate));
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(p => new Date(p.timestamp) <= end);
    }

    if (filters.doctorName) {
      result = result.filter(p => (p.doctor?.name || p.doctorName) === filters.doctorName);
    }

    if (filters.lensType) {
      result = result.filter(p => p.lensTypes && p.lensTypes.includes(filters.lensType));
    }

    return result;
  }, [prescriptions, filters]);

  const paginatedData = filteredData.slice(0, filters.recordsPerPage);

  // --- DASHBOARD AGGREGATIONS (Adapted for Single Store) ---
  const dashboardStats = useMemo(() => {
    const total = filteredData.length;
    const doctorCounts = {};
    
    // Grouping by Doctor instead of Store since this is a single store view
    filteredData.forEach(p => {
      const docName = p.doctor?.name || p.doctorName || "Unknown Optometrist";
      doctorCounts[docName] = (doctorCounts[docName] || 0) + 1;
    });

    const pieData = Object.keys(doctorCounts).map(key => ({ name: key, value: doctorCounts[key] }));

    return { total, pieData, doctorSummary: doctorCounts };
  }, [filteredData]);

  // --- ACTIONS ---
  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this prescription record permanently?`)) return;
    try { await deleteDoc(doc(db, "stores", activeBranchId, "prescriptions", id)); } 
    catch (error) { alert("Error deleting record."); }
  };

  const handleOpenAddModal = () => {
    if (!activeBranchId) return alert("System Error: No active store targeted.");
    setFormData(INITIAL_FORM_STATE);
    setEditingId(null);
    setActiveTab("eyewear");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setFormData({
      patientName: record.patient?.name || record.name || "",
      patientMobile: record.patient?.mobile || record.phone || "",
      patientAge: record.patient?.age || "",
      patientGender: record.patient?.gender || "Male",
      doctorName: record.doctor?.name || record.doctorName || "",
      prescriptionNotes: record.prescriptionNotes || "",
      sphRight: record.sphRight || "", cylRight: record.cylRight || "", axisRight: record.axisRight || "", pdRight: record.pdRight || "", vaRight: record.vaRight || "", addRight: record.addRight || "",
      nvSphRight: record.nvSphRight || "", nvCylRight: record.nvCylRight || "", nvAxisRight: record.nvAxisRight || "",
      sphLeft: record.sphLeft || "", cylLeft: record.cylLeft || "", axisLeft: record.axisLeft || "", pdLeft: record.pdLeft || "", vaLeft: record.vaLeft || "", addLeft: record.addLeft || "",
      nvSphLeft: record.nvSphLeft || "", nvCylLeft: record.nvCylLeft || "", nvAxisLeft: record.nvAxisLeft || "",
      clSphRight: record.clSphRight || "", clCylRight: record.clCylRight || "", clAxisRight: record.clAxisRight || "",
      clSphLeft: record.clSphLeft || "", clCylLeft: record.clCylLeft || "", clAxisLeft: record.clAxisLeft || "",
      lensTypes: record.lensTypes || []
    });
    setEditingId(record.id);
    setActiveTab("eyewear");
    setIsModalOpen(true);
  };

  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleLensType = (type) => {
    setFormData(prev => {
      const exists = prev.lensTypes.includes(type);
      return { ...prev, lensTypes: exists ? prev.lensTypes.filter(t => t !== type) : [...prev.lensTypes, type] };
    });
  };

  const handleSavePrescription = async () => {
    if (!formData.patientName && !formData.patientMobile) return alert("Patient Name or Mobile is required.");
    
    const payload = {
      patient: { name: formData.patientName, mobile: formData.patientMobile, age: formData.patientAge, gender: formData.patientGender },
      doctor: { name: formData.doctorName },
      prescriptionNotes: formData.prescriptionNotes, lensTypes: formData.lensTypes,
      sphRight: formData.sphRight, cylRight: formData.cylRight, axisRight: formData.axisRight, pdRight: formData.pdRight, vaRight: formData.vaRight, addRight: formData.addRight,
      nvSphRight: formData.nvSphRight, nvCylRight: formData.nvCylRight, nvAxisRight: formData.nvAxisRight,
      sphLeft: formData.sphLeft, cylLeft: formData.cylLeft, axisLeft: formData.axisLeft, pdLeft: formData.pdLeft, vaLeft: formData.vaLeft, addLeft: formData.addLeft,
      nvSphLeft: formData.nvSphLeft, nvCylLeft: formData.nvCylLeft, nvAxisLeft: formData.nvAxisLeft,
      clSphRight: formData.clSphRight, clCylRight: formData.clCylRight, clAxisRight: formData.clAxisRight,
      clSphLeft: formData.clSphLeft, clCylLeft: formData.clCylLeft, clAxisLeft: formData.clAxisLeft,
      timestamp: new Date().toISOString()
    };

    try {
      if (editingId) await updateDoc(doc(db, "stores", activeBranchId, "prescriptions", editingId), payload);
      else await addDoc(collection(db, "stores", activeBranchId, "prescriptions"), payload);
      setIsModalOpen(false);
    } catch (e) { alert("Failed to save prescription: " + e.message); }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '');
  };

  // --- PRINT GLOBAL DASHBOARD PDF ---
  const handleExportDashboardPDF = () => {
    const printWindow = window.open('', '_blank');
    const dateStr = new Date().toLocaleString();
    
    let tableRows = filteredData.map((record, index) => {
      return `
        <tr>
          <td>${index + 1}</td>
          <td>${record.patient?.name || record.name || '-'}</td>
          <td>${record.patient?.mobile || record.phone || '-'}</td>
          <td>${record.doctor?.name || record.doctorName || '-'}</td>
          <td>${formatDate(record.timestamp)}</td>
        </tr>
      `;
    }).join('');

    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <style>
      * { font-family: Arial, sans-serif; }
      @page { size: A4 landscape; margin: 15mm; }
      body { padding: 20px; -webkit-print-color-adjust: exact; }
      h1 { color: #16205f; text-align: center; }
      h2 { color: #333; text-align: center; font-size: 16px; margin-bottom: 5px; }
      .meta { text-align: center; color: #555; margin-bottom: 30px; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
      th { background: #16205f; color: white; text-transform: uppercase; }
      tr:nth-child(even) { background-color: #f2f2f2; }
    </style>
    </head>
    <body>
      <h1>${activeStoreName}</h1>
      <h2>Clinical Prescription Report</h2>
      <div class="meta">Generated on: ${dateStr} | Total Records: ${filteredData.length}</div>
      <table>
        <thead>
          <tr>
            <th>#</th><th>Patient Name</th><th>Mobile</th><th>Optometrist</th><th>Date/Time</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      <script>window.onload = function() { window.print(); setTimeout(() => window.close(), 500); }</script>
    </body>
    </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // --- A4 PDF PRINTER ENGINE ---
  const handlePrintPrescription = (record) => {
    // Alert prompt for Signatures
    const includeSignatures = window.confirm("Include Optometrist and Authorized Signatures in the printout?");
    
    const printWindow = window.open('', '_blank');
    const dateObj = new Date(record.timestamp || Date.now());
    const dateStr = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const nextMonthStr = new Date(dateObj.setMonth(dateObj.getMonth() + 1)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    
    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Prescription - ${record.patient?.name || record.name || 'Patient'}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; }
      @page { size: A4; margin: 15mm; padding: 10mm; }
      body { background: #fff; color: #222; -webkit-print-color-adjust: exact; }
      .a4-page { width: 100%; margin: 0 auto; background: white; }
      
.header {
  position: relative;
  text-align: center;
  border-bottom: 2px solid #16205f;
  padding-bottom: 15px;
  margin-bottom: 20px;
  min-height: 90px;
}

.logo {
  position: absolute;
  left: 0;
  top: 0;
}

.logo img {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.header-content {
  display: inline-block;
  text-align: center;
}

.clinic-name {
  font-size: 26px;
  font-weight: 900;
  color: #16205f;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.store-address {
  font-size: 12px;
  color: #555;
  margin-bottom: 8px;
}

.tagline {
  color: #555;
  font-size: 12px;
  margin-bottom: 10px;
}

.title {
  display: inline-block;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  background: #16205f;
  padding: 6px 20px;
  border-radius: 20px;
  text-transform: uppercase;
  margin-bottom: 10px;
}
      .patient-section { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px; }
      .info-box { border: 1px solid #e4e4e4; border-radius: 8px; padding: 15px; flex: 1; background: #fafafa; }
      .info-box h4 { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
      .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
      .info-row strong { color: #111; }
      
      .section { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 25px; overflow: hidden; }
      .section-title { background: #f0f4f8; padding: 10px 15px; font-size: 14px; font-weight: bold; border-bottom: 1px solid #ddd; color: #16205f; text-transform: uppercase; }
      
      table { width: 100%; border-collapse: collapse; }
      th { background: #fafafa; font-size: 11px; padding: 10px; border: 1px solid #ddd; text-transform: uppercase; color: #444; }
      td { text-align: center; padding: 10px; border: 1px solid #ddd; font-size: 13px; font-weight: 600; color: #111; }
      
      .notes { padding: 15px; border: 1px dashed #bbb; border-radius: 8px; font-size: 13px; line-height: 1.6; background: #fffdf9; margin-bottom: 20px; }
      .notes h3 { margin-bottom: 8px; font-size: 14px; color: #16205f; text-transform: uppercase; }
      
      .footer-reminder { display: flex; justify-content: space-between; align-items: center; border: 1px solid #ddd; border-radius: 8px; padding: 15px 20px; background: #f8f9fa; margin-bottom: 30px; }
      .footer-reminder .text { font-size: 13px; color: #555; text-transform: uppercase; font-weight: bold; }
      .footer-reminder .date { font-size: 20px; font-weight: 900; color: #b40000; }
      
      .signature-area { display: flex; justify-content: space-between; margin-top: 50px; padding: 0 20px; }
      .signature { text-align: center; width: 200px; font-size: 12px; color: #555; font-weight: bold; }
      .signature-line { border-top: 1px solid #000; margin-bottom: 8px; }
      
      .footer { text-align: center; margin-top: 40px; color: #888; font-size: 10px; border-top: 1px solid #eee; padding-top: 10px; }
    </style>
    </head>
    <body>
      <div class="a4-page">
        
         <div class="header">
  <div class="logo">
    <img src="${window.location.origin}/logo192.png" alt="Logo" />
  </div>

  <div class="header-content">
    <div class="clinic-name">Shruti Eye Care & Opticals</div>

<div class="store-address">  ${activeStoreAdress}</br>
      Mobile No: 9470835831, 9939605949 | Landline: ${activeStoreNumber}
    </div>

    <div class="tagline">
      Complete Eye Care • Computerized Eye Testing • Contact Lens • Premium Frames
    </div>

    <div class="title">Eye Test Prescription</div>
  </div>
  <br />

        <div class="patient-section">
          <div class="info-box">
            <h4>Patient Profile</h4>
            <div class="info-row"><span>Name:</span> <strong>${record.patient?.name || record.name || 'Walk-in Patient'}</strong></div>
            <div class="info-row"><span>Mobile:</span> <strong>${record.patient?.mobile || record.phone || 'N/A'}</strong></div>
            <div class="info-row"><span>Age / Gender:</span> <strong>${record.patient?.age || '-'} / ${record.patient?.gender || '-'}</strong></div>
          </div>
          <div class="info-box">
            <h4>Clinical Details</h4>
            <div class="info-row"><span>Date:</span> <strong>${dateStr}</strong></div>
            <div class="info-row"><span>Optometrist:</span> <strong>${record.doctor?.name || record.doctorName || activeStoreName}</strong></div>
            <div class="info-row"><span>Lens Advised:</span> <strong>${record.lensTypes?.join(', ') || 'As per prescription'}</strong></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Optical Measurement Matrix</div>
          <table>
            <tr>
              <th rowspan="2" style="background:#eef2f6;">EYE</th>
              <th colspan="4">DISTANCE VISION (DV)</th>
              <th colspan="3">NEAR VISION (NV)</th>
              <th rowspan="2" style="background:#eef2f6;">ADD</th>
            </tr>
            <tr>
              <th>SPH</th><th>CYL</th><th>AXIS</th><th>VA</th>
              <th>SPH</th><th>CYL</th><th>AXIS</th>
            </tr>
            <tr>
              <td style="background:#eef2f6;">RIGHT (OD)</td>
              <td>${record.sphRight || '-'}</td><td>${record.cylRight || '-'}</td><td>${record.axisRight || '-'}</td><td>${record.vaRight || '-'}</td>
              <td>${record.nvSphRight || '-'}</td><td>${record.nvCylRight || '-'}</td><td>${record.nvAxisRight || '-'}</td>
              <td style="color:#b40000;">${record.addRight || '-'}</td>
            </tr>
            <tr>
              <td style="background:#eef2f6;">LEFT (OS)</td>
              <td>${record.sphLeft || '-'}</td><td>${record.cylLeft || '-'}</td><td>${record.axisLeft || '-'}</td><td>${record.vaLeft || '-'}</td>
              <td>${record.nvSphLeft || '-'}</td><td>${record.nvCylLeft || '-'}</td><td>${record.nvAxisLeft || '-'}</td>
              <td style="color:#b40000;">${record.addLeft || '-'}</td>
            </tr>
          </table>
        </div>

        ${(record.clSphRight || record.clSphLeft) ? `
        <div class="section">
          <div class="section-title">Contact Lens Specification</div>
          <table>
            <tr><th style="background:#eef2f6;">EYE</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr>
            <tr><td style="background:#eef2f6;">RIGHT (OD)</td><td>${record.clSphRight || '-'}</td><td>${record.clCylRight || '-'}</td><td>${record.clAxisRight || '-'}</td></tr>
            <tr><td style="background:#eef2f6;">LEFT (OS)</td><td>${record.clSphLeft || '-'}</td><td>${record.clCylLeft || '-'}</td><td>${record.clAxisLeft || '-'}</td></tr>
          </table>
        </div>` : ''}

        <div class="notes">
          <h3>Clinical Remarks</h3>
          <p>${record.prescriptionNotes || "• Use prescribed glasses regularly to prevent eye strain.<br>• Maintain proper screen distance and take breaks.<br>• Please bring this card on your next visit."}</p>
        </div>

        <div class="footer-reminder">
          <div class="text">Recommended Next Eye Checkup By:</div>
          <div class="date">${nextMonthStr}</div>
        </div>

        ${includeSignatures ? `
        <div class="signature-area">
          <div class="signature"><div class="signature-line"></div>Optometrist Signature</div>
          <div class="signature"><div class="signature-line"></div>Authorized Signatory</div>
        </div>
        ` : ''}

        <div class="footer">
          © ${activeStoreName} | Generated via Store Management System
        </div>
      </div>
      <script>
        // Ensure image loads perfectly before printing window triggers
        window.addEventListener('load', function() {
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 300);
        });
      </script>
    </body>
    </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans text-base">
      <StoreSidebar />

      {/* MODAL OVERLAY: ADD / EDIT PRESCRIPTION */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <Stethoscope className="text-blue-600" /> {editingId ? "Edit Prescription" : "Add New Prescription"}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition text-slate-500 hover:text-rose-500">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              
              {/* Top Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-5 rounded-xl border border-slate-200">
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase text-slate-500 flex items-center gap-2"><UserIcon size={14}/> Patient Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Name</label><input type="text" value={formData.patientName} onChange={e => handleFormChange('patientName', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Mobile</label><input type="tel" value={formData.patientMobile} onChange={e => handleFormChange('patientMobile', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500 font-mono" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Age</label><input type="text" value={formData.patientAge} onChange={e => handleFormChange('patientAge', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Gender</label><select value={formData.patientGender} onChange={e => handleFormChange('patientGender', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500"><option>Male</option><option>Female</option><option>Other</option></select></div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase text-slate-500 flex items-center gap-2"><Activity size={14}/> Clinic Info</h3>
                  <div className="space-y-3">
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Doctor / Optometrist Name</label><input type="text" value={formData.doctorName} onChange={e => handleFormChange('doctorName', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Lens Requirements</label>
                      <div className="flex flex-wrap gap-2">
                        {ALL_LENS_TYPES.map(type => (
                          <label key={type} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                            <input type="checkbox" checked={formData.lensTypes.includes(type)} onChange={() => toggleLensType(type)} className="accent-blue-600"/> {type}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <button onClick={() => setActiveTab("eyewear")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition ${activeTab === "eyewear" ? "bg-slate-100 text-blue-600 border border-b-0 border-slate-200" : "text-slate-500"}`}>Eyewear Power</button>
                <button onClick={() => setActiveTab("contact")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition ${activeTab === "contact" ? "bg-slate-100 text-blue-600 border border-b-0 border-slate-200" : "text-slate-500"}`}>Contact Lens</button>
              </div>

              {/* Power Matrix Form */}
              {activeTab === "eyewear" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-slate-200 p-4 rounded-xl space-y-4 bg-slate-50">
                    <h4 className="text-xs font-black uppercase text-blue-600">Right Eye (OD)</h4>
                    <div className="grid grid-cols-4 gap-2 text-center text-[10px] font-bold text-slate-500"><span>SPH</span><span>CYL</span><span>AXIS</span><span>VA</span></div>
                    <div className="grid grid-cols-4 gap-2"><input placeholder="0.00" value={formData.sphRight} onChange={e=>handleFormChange('sphRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="0.00" value={formData.cylRight} onChange={e=>handleFormChange('cylRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="0" value={formData.axisRight} onChange={e=>handleFormChange('axisRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="VA" value={formData.vaRight} onChange={e=>handleFormChange('vaRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/></div>
                    <div className="text-[10px] font-bold text-slate-500 mt-2">NEAR (NV)</div>
                    <div className="grid grid-cols-3 gap-2"><input placeholder="SPH" value={formData.nvSphRight} onChange={e=>handleFormChange('nvSphRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="CYL" value={formData.nvCylRight} onChange={e=>handleFormChange('nvCylRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="AXIS" value={formData.nvAxisRight} onChange={e=>handleFormChange('nvAxisRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/></div>
                    <div className="grid grid-cols-2 gap-2 mt-2"><div><span className="text-[10px] font-bold text-slate-500 block mb-1">ADD</span><input value={formData.addRight} onChange={e=>handleFormChange('addRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded outline-none focus:border-blue-500 text-sm font-mono"/></div><div><span className="text-[10px] font-bold text-slate-500 block mb-1">PD</span><input value={formData.pdRight} onChange={e=>handleFormChange('pdRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded outline-none focus:border-blue-500 text-sm font-mono"/></div></div>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-xl space-y-4 bg-slate-50">
                    <h4 className="text-xs font-black uppercase text-blue-600">Left Eye (OS)</h4>
                    <div className="grid grid-cols-4 gap-2 text-center text-[10px] font-bold text-slate-500"><span>SPH</span><span>CYL</span><span>AXIS</span><span>VA</span></div>
                    <div className="grid grid-cols-4 gap-2"><input placeholder="0.00" value={formData.sphLeft} onChange={e=>handleFormChange('sphLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="0.00" value={formData.cylLeft} onChange={e=>handleFormChange('cylLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="0" value={formData.axisLeft} onChange={e=>handleFormChange('axisLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="VA" value={formData.vaLeft} onChange={e=>handleFormChange('vaLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/></div>
                    <div className="text-[10px] font-bold text-slate-500 mt-2">NEAR (NV)</div>
                    <div className="grid grid-cols-3 gap-2"><input placeholder="SPH" value={formData.nvSphLeft} onChange={e=>handleFormChange('nvSphLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="CYL" value={formData.nvCylLeft} onChange={e=>handleFormChange('nvCylLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/><input placeholder="AXIS" value={formData.nvAxisLeft} onChange={e=>handleFormChange('nvAxisLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-blue-500 text-sm font-mono"/></div>
                    <div className="grid grid-cols-2 gap-2 mt-2"><div><span className="text-[10px] font-bold text-slate-500 block mb-1">ADD</span><input value={formData.addLeft} onChange={e=>handleFormChange('addLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded outline-none focus:border-blue-500 text-sm font-mono"/></div><div><span className="text-[10px] font-bold text-slate-500 block mb-1">PD</span><input value={formData.pdLeft} onChange={e=>handleFormChange('pdLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded outline-none focus:border-blue-500 text-sm font-mono"/></div></div>
                  </div>
                </div>
              )}

              {activeTab === "contact" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div className="border border-slate-200 p-4 rounded-xl space-y-4 bg-slate-50">
                    <h4 className="text-xs font-black uppercase text-purple-600">Right Contact (OD)</h4>
                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-bold text-slate-500"><span>SPH</span><span>CYL</span><span>AXIS</span></div>
                    <div className="grid grid-cols-3 gap-2"><input placeholder="0.00" value={formData.clSphRight} onChange={e=>handleFormChange('clSphRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/><input placeholder="0.00" value={formData.clCylRight} onChange={e=>handleFormChange('clCylRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/><input placeholder="0" value={formData.clAxisRight} onChange={e=>handleFormChange('clAxisRight', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/></div>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-xl space-y-4 bg-slate-50">
                    <h4 className="text-xs font-black uppercase text-purple-600">Left Contact (OS)</h4>
                    <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-bold text-slate-500"><span>SPH</span><span>CYL</span><span>AXIS</span></div>
                    <div className="grid grid-cols-3 gap-2"><input placeholder="0.00" value={formData.clSphLeft} onChange={e=>handleFormChange('clSphLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/><input placeholder="0.00" value={formData.clCylLeft} onChange={e=>handleFormChange('clCylLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/><input placeholder="0" value={formData.clAxisLeft} onChange={e=>handleFormChange('clAxisLeft', e.target.value)} className="w-full p-2 border border-slate-200 rounded text-center outline-none focus:border-purple-500 text-sm font-mono"/></div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Clinical Remarks / Notes</label>
                <textarea value={formData.prescriptionNotes} onChange={e => handleFormChange('prescriptionNotes', e.target.value)} rows="3" className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-sm"></textarea>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-xl font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition">Cancel</button>
              <button onClick={handleSavePrescription} className="px-8 py-2.5 rounded-xl font-black uppercase tracking-wide text-white bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/30 transition">Save Data</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 p-6 lg:p-10 space-y-8 overflow-x-hidden">
        
        {/* HEADER - Light Theme */}
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-3">
              <Activity className="text-blue-600" size={32} /> Clinical Prescriptions
            </h1>
            <p className="text-slate-500 mt-1.5 text-sm font-medium">Manage optical measurements, contact lenses, and patient histories.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={handleExportDashboardPDF} className="px-4 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold text-sm transition-colors shadow-sm flex items-center gap-2">
              <Download size={18} /> Export Report
            </button>
            <button onClick={handleOpenAddModal} className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors shadow-sm flex items-center gap-2">
              <Plus size={18} /> Add Prescription
            </button>
            <div className="bg-blue-50 px-4 py-3 rounded-xl border border-blue-200 text-blue-700 font-black text-sm">
              {activeStoreName}
            </div>
          </div>
        </div>

        {/* DASHBOARD WIDGETS (Store Specific) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pie Chart Widget */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm lg:col-span-1">
            <h3 className="text-sm font-black uppercase text-slate-500 flex items-center gap-2 mb-4">
              <PieChartIcon size={16} className="text-blue-500"/> Prescriptions By Optometrist
            </h3>
            <div className="h-64 w-full">
              {dashboardStats.pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dashboardStats.pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                      {dashboardStats.pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Summary Table Widget */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm lg:col-span-2 overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-black uppercase text-slate-500 flex items-center gap-2">
                <Activity size={16} className="text-blue-500"/> Store Optometrist Summary
              </h3>
            </div>
            <div className="p-0 overflow-y-auto max-h-64 flex-1">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-white sticky top-0 border-b border-slate-200">
                  <tr>
                    <th className="p-4 font-bold text-slate-600">Optometrist Name</th>
                    <th className="p-4 font-bold text-slate-600">Total Rx</th>
                    <th className="p-4 font-bold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(dashboardStats.doctorSummary).map(([doctorName, count], idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="p-4 font-bold text-slate-800">{doctorName}</td>
                      <td className="p-4 text-blue-600 font-black">{count}</td>
                      <td className="p-4"><span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">Active</span></td>
                    </tr>
                  ))}
                  {Object.keys(dashboardStats.doctorSummary).length === 0 && (
                    <tr><td colSpan="3" className="p-6 text-center text-slate-400">No active prescriptions found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* SEARCH & FILTER CONSOLE */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Filter Records</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-1.5 focus-within:border-blue-500 transition-colors">
              <Search className="text-slate-400" size={16} />
              <input 
                type="text" 
                placeholder="Search Name or Phone..." 
                value={filters.query} 
                onChange={e => setFilters({...filters, query: e.target.value})} 
                className="bg-transparent border-none outline-none w-full text-xs py-1.5 text-slate-800 font-medium" 
              />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <Calendar size={14} className="text-slate-400"/>
              <span>From:</span>
              <input type="date" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} className="bg-transparent outline-none font-mono cursor-pointer w-full" />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <Calendar size={14} className="text-slate-400"/>
              <span>To:</span>
              <input type="date" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} className="bg-transparent outline-none font-mono cursor-pointer w-full" />
            </div>

            <select value={filters.doctorName} onChange={e => setFilters({...filters, doctorName: e.target.value})} className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none text-slate-800 cursor-pointer text-xs font-bold">
              <option value="">All Optometrists</option>
              {uniqueDoctors.map(doc => <option key={doc} value={doc}>{doc}</option>)}
            </select>

            <select value={filters.lensType} onChange={e => setFilters({...filters, lensType: e.target.value})} className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none text-slate-800 cursor-pointer text-xs font-bold">
              <option value="">All Lens Types</option>
              {ALL_LENS_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>

          <div className="flex justify-between items-center pt-2">
            <div className="flex items-center gap-3">
              <span className="text-slate-500 text-xs font-bold">Display:</span>
              <select value={filters.recordsPerPage} onChange={e => setFilters({...filters, recordsPerPage: Number(e.target.value)})} className="p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none text-slate-800 cursor-pointer text-xs font-bold">
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} Records</option>)}
              </select>
            </div>
            {(filters.query || filters.startDate || filters.endDate || filters.doctorName || filters.lensType) && (
               <button onClick={() => setFilters({ query: "", startDate: "", endDate: "", doctorName: "", lensType: "", recordsPerPage: 10 })} className="text-xs font-bold text-rose-500 hover:text-rose-600 transition">
                 Reset Filters
               </button>
            )}
          </div>
        </div>

        {/* PRESCRIPTION DATA MATRIX - Compact Card Layout */}
        <div className="space-y-5">
          {paginatedData.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-16 text-center text-slate-500 text-lg font-medium shadow-sm">
              No prescription records found matching your filters.
            </div>
          ) : (
            paginatedData.map((record, index) => (
              <div key={record.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col xl:flex-row hover:shadow-md transition-shadow">
                
                {/* CUSTOMER DETAILS COLUMN */}
                <div className="xl:w-1/4 bg-slate-50/50 p-5 border-b xl:border-b-0 xl:border-r border-slate-200 relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-black text-lg border border-blue-100">{index + 1}</div>
                    <div>
                      <h3 className="text-slate-800 font-black text-lg leading-none">{record.patient?.name || record.name || "Walk-in Patient"}</h3>
                      <span className="text-[11px] text-slate-400 font-mono font-bold uppercase tracking-wider">ID: {record.id.slice(-6)}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-600 font-medium">
                    <p><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Mobile</strong> <span className="font-mono text-slate-800 font-bold">{record.patient?.mobile || record.phone || "-"}</span></p>
                    <p className="pt-1.5"><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Doctor</strong> <span className="text-slate-800">{record.doctor?.name || record.doctorName || "-"}</span></p>
                    <p className="pt-1.5"><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Date</strong> <span className="font-mono text-slate-800">{formatDate(record.timestamp)}</span></p>
                  </div>
                </div>

                {/* EYEWEAR POWER DETAILS COLUMN */}
                <div className="xl:w-1/2 p-5 border-b xl:border-b-0 xl:border-r border-slate-200 bg-white">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Eye size={14} className="text-blue-500"/> Eyewear Power</h4>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-center text-[10px] font-mono border-collapse">
                      <thead>
                        <tr>
                          <th className="border-b border-r border-slate-200 bg-slate-50 p-2 text-slate-500 font-bold" rowSpan={2}>TYPE</th>
                          <th className="border-b border-r border-slate-200 bg-blue-50 text-blue-700 p-2 font-black" colSpan={5}>RIGHT EYE (OD)</th>
                          <th className="border-b border-slate-200 bg-purple-50 text-purple-700 p-2 font-black" colSpan={5}>LEFT EYE (OS)</th>
                        </tr>
                        <tr className="bg-slate-50 text-slate-500 uppercase">
                          <th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">AXIS</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">PD</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">VA</th>
                          <th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">AXIS</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">PD</th><th className="border-b border-slate-200 p-1.5 font-bold">VA</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-800">
                        <tr className="hover:bg-slate-50/50">
                          <td className="border-b border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">DV</td>
                          <td className="border-b border-r border-slate-200 p-2 font-bold">{record.sphRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.cylRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.axisRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.pdRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.vaRight || "-"}</td>
                          <td className="border-b border-r border-slate-200 p-2 font-bold">{record.sphLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.cylLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.axisLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.pdLeft || "-"}</td><td className="border-b border-slate-200 p-2 font-bold">{record.vaLeft || "-"}</td>
                        </tr>
                        <tr className="hover:bg-slate-50/50">
                          <td className="border-b border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">NV</td>
                          <td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvSphRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvCylRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvAxisRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 bg-slate-50 text-slate-300" colSpan={2}>-</td>
                          <td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvSphLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvCylLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvAxisLeft || "-"}</td><td className="border-b border-slate-200 p-2 bg-slate-50 text-slate-300" colSpan={2}>-</td>
                        </tr>
                        <tr className="hover:bg-slate-50/50">
                          <td className="border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">ADD</td>
                          <td className="border-r border-slate-200 p-2 text-blue-600 font-black" colSpan={5}>{record.addRight || "-"}</td>
                          <td className="p-2 text-purple-600 font-black" colSpan={5}>{record.addLeft || "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {record.lensTypes && record.lensTypes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {record.lensTypes.map((type, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-[9px] uppercase font-bold tracking-wider rounded border bg-slate-50 text-slate-600 border-slate-200">{type}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* CONTACT LENS & ACTIONS COLUMN */}
                <div className="xl:w-1/4 p-5 flex flex-col justify-between bg-white">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Users size={14} className="text-blue-500"/> Contact Lens</h4>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-center text-[10px] font-mono border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500">
                            <th className="border-b border-r border-slate-200 p-1.5 font-bold">EYE</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-slate-200 p-1.5 font-bold">AXIS</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-800">
                          <tr className="hover:bg-slate-50/50"><td className="border-b border-r border-slate-200 p-1.5 font-black bg-blue-50 text-blue-700">OD</td><td className="border-b border-r border-slate-200 p-1.5 font-bold">{record.clSphRight || "-"}</td><td className="border-b border-r border-slate-200 p-1.5 font-bold">{record.clCylRight || "-"}</td><td className="border-b border-slate-200 p-1.5 font-bold">{record.clAxisRight || "-"}</td></tr>
                          <tr className="hover:bg-slate-50/50"><td className="border-r border-slate-200 p-1.5 font-black bg-purple-50 text-purple-700">OS</td><td className="border-r border-slate-200 p-1.5 font-bold">{record.clSphLeft || "-"}</td><td className="border-r border-slate-200 p-1.5 font-bold">{record.clCylLeft || "-"}</td><td className="p-1.5 font-bold">{record.clAxisLeft || "-"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                     <button onClick={() => handleOpenEditModal(record)} className="p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 transition-colors" title="Edit Prescription"><Edit3 size={16} /></button>
                     <button onClick={() => handleDelete(record.id)} className="p-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-xl text-rose-600 transition-colors" title="Delete Prescription"><Trash2 size={16} /></button>
                     <button onClick={() => handlePrintPrescription(record)} className="flex-1 p-2 bg-blue-600 hover:bg-blue-700 rounded-xl text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm flex items-center justify-center gap-2">
                       <Printer size={14}/> Print
                     </button>
                  </div>
                </div>

              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}